import { MonitoringCategory } from "@domain/product/monitoring/monitoring-product.service";
import { FgdsProductType } from "@domain/product/product";
import { FgdsProject } from "@domain/project/fgds-project";
import { MonitoringProject } from "@domain/project/monitoring-project";
import { isFgdsProject, isMonitoringProject, type ScopedProject } from "@domain/project/project-guards";
import { BusinessScope } from "@lib/constants/business-scope";
import { instanceToPlain, plainToInstance } from "class-transformer";

export function requireScopedProject(project: unknown): ScopedProject {
  if (isFgdsProject(project) || isMonitoringProject(project)) {
    return project;
  }
  throw new Error("Expected a scoped project instance");
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function inferScopeSpecificKeys() {
  const fgdsSerialized = instanceToPlain(FgdsProject.create("__template__", "Draft", "DE"));
  const monitoringSerialized = instanceToPlain(MonitoringProject.create("__template__", "Draft", "DE"));

  const fgdsKeys = isRecord(fgdsSerialized) ? new Set(Object.keys(fgdsSerialized)) : new Set<string>();
  const monitoringKeys = isRecord(monitoringSerialized) ? new Set(Object.keys(monitoringSerialized)) : new Set<string>();

  return {
    fgdsOnly: new Set([...fgdsKeys].filter((key) => !monitoringKeys.has(key))),
    monitoringOnly: new Set([...monitoringKeys].filter((key) => !fgdsKeys.has(key))),
  };
}

const scopeSpecificKeys = inferScopeSpecificKeys();

export function getScopeSpecificKeysForTesting() {
  return {
    fgdsOnly: [...scopeSpecificKeys.fgdsOnly].sort(),
    monitoringOnly: [...scopeSpecificKeys.monitoringOnly].sort(),
  };
}

function hasMeaningfulScopeValue(value: unknown): boolean {
  if (Array.isArray(value)) return value.length > 0;
  if (value && typeof value === "object") return Object.keys(value).length > 0;
  return value !== undefined && value !== null && value !== "";
}

function scoreScopeKeys(plain: Record<string, unknown>, keys: ReadonlySet<string>) {
  return [...keys].reduce((score, key) => {
    if (!(key in plain)) return score;
    return score + 1 + (hasMeaningfulScopeValue(plain[key]) ? 2 : 0);
  }, 0);
}

export function inferProjectScopeFromPayload(plain: Record<string, unknown>): BusinessScope | undefined {
  const fgdsScore = scoreScopeKeys(plain, scopeSpecificKeys.fgdsOnly);
  const monitoringScore = scoreScopeKeys(plain, scopeSpecificKeys.monitoringOnly);

  if (fgdsScore === 0 && monitoringScore === 0) return undefined;
  if (fgdsScore > monitoringScore) return BusinessScope.FGDS;
  if (monitoringScore > fgdsScore) return BusinessScope.MONITORING;
  return undefined;
}

function sanitizeScopedPayload(plain: Record<string, unknown>, scope: BusinessScope) {
  const scopedPayload = { ...plain };
  const keysToStrip = scope === BusinessScope.MONITORING ? scopeSpecificKeys.fgdsOnly : scopeSpecificKeys.monitoringOnly;
  keysToStrip.forEach((key) => delete scopedPayload[key]);
  return scopedPayload;
}

/**
 * Migrates old floorplan-level groups (pre-grouping-refactor) to project-level groups.
 * Old format: floorplan.groups[].{ id, name, groupId, patientMonitors[], alarmDevices[], ... }
 * New format: project.groups[].{ id, name, groupId, floorplanIds[], references[] }
 *
 * The API passes the raw JSON through unchanged. The frontend is responsible for this migration.
 */
function hasOldFloorplanGroups(floorplans: unknown[]): boolean {
  return floorplans.some((fp) => {
    if (!isRecord(fp)) return false;
    const groups = fp["groups"];
    if (!Array.isArray(groups) || groups.length === 0) return false;
    // Old groups contain product arrays (alarmDevices, patientMonitors, …) or at least exist
    return groups.some(isRecord);
  });
}

function projectGroupsHaveReferences(groups: unknown[]): boolean {
  return groups.some((g) => isRecord(g) && Array.isArray(g["references"]) && (g["references"] as unknown[]).length > 0);
}

const FGDS_ITEM_FIELDS: [string, FgdsProductType][] = [
  ["alarmDevices", FgdsProductType.ALARMDEVICE],
  ["alarmDevicePlaceholders", FgdsProductType.ALARMDEVICE],
  ["gasWarningCenters", FgdsProductType.GASWARNINGCENTER],
  ["gasWarningCenterPlaceholders", FgdsProductType.GASWARNINGCENTER],
  ["plasticSigns", FgdsProductType.PLASTICSIGN],
  ["plasticSignPlaceholders", FgdsProductType.PLASTICSIGN],
  ["signalElements", FgdsProductType.SIGNALELEMENT],
  ["signalElementPlaceholders", FgdsProductType.SIGNALELEMENT],
  ["transmitters", FgdsProductType.TRANSMITTER],
  ["transmitterPlaceholders", FgdsProductType.TRANSMITTER],
];

const MONITORING_ITEM_FIELDS: [string, MonitoringCategory][] = [
  ["patientMonitors", MonitoringCategory.PATIENT_MONITORS],
  ["patientMonitorPlaceholders", MonitoringCategory.PATIENT_MONITORS],
  ["centralStations", MonitoringCategory.CENTRAL_STATIONS],
  ["centralStationPlaceholders", MonitoringCategory.CENTRAL_STATIONS],
  ["telemetries", MonitoringCategory.TELEMETRY],
  ["telemetryPlaceholders", MonitoringCategory.TELEMETRY],
  ["kvms", MonitoringCategory.KVMS],
  ["kvmPlaceholders", MonitoringCategory.KVMS],
  ["displays", MonitoringCategory.DISPLAYS],
  ["displayPlaceholders", MonitoringCategory.DISPLAYS],
  ["utilities", MonitoringCategory.UTILITY],
  ["utilityPlaceholders", MonitoringCategory.UTILITY],
];

/**
 * Applies the group migration logic to a project plain object using explicitly provided floorplan
 * raw data. This is used when floorplans have already been extracted from the project payload and
 * stored separately (e.g. in STORE_NAME_PROJECT_FLOORPLANS), so plain["floorplans"] is absent.
 *
 * Returns an object with:
 * - `projectPlain`: the updated project plain with migrated groups
 * - `migratedFloorplains`: the migrated floorplan raws (groups removed, products lifted)
 */
export function applyFloorplanGroupMigrationWithExternalFloorplans(
  plain: Record<string, unknown>,
  externalFloorplains: Record<string, unknown>[],
  scope: BusinessScope,
): { projectPlain: Record<string, unknown>; migratedFloorplains: Record<string, unknown>[] } | null {
  if (externalFloorplains.length === 0 || !hasOldFloorplanGroups(externalFloorplains)) {
    return null;
  }

  const existingGroups = plain["groups"];
  if (Array.isArray(existingGroups) && existingGroups.length > 0) {
    if (projectGroupsHaveReferences(existingGroups) || !hasOldFloorplanGroups(externalFloorplains)) {
      return null;
    }
  }

  const merged = migrateOldFloorplanGroupsToProjectGroups({ ...plain, floorplans: externalFloorplains }, scope);
  const migratedFloorplains = Array.isArray(merged["floorplans"])
    ? (merged["floorplans"] as Record<string, unknown>[])
    : externalFloorplains;
  const projectPlain = { ...merged };
  delete projectPlain["floorplans"];

  return { projectPlain, migratedFloorplains };
}

function migrateOldFloorplanGroupsToProjectGroups(plain: Record<string, unknown>, scope: BusinessScope): Record<string, unknown> {
  const floorplans = plain["floorplans"];
  if (!Array.isArray(floorplans)) return plain;

  const existingGroups = plain["groups"];
  // Already fully migrated: project has groups with references and no old floorplan-groups remain
  if (Array.isArray(existingGroups) && existingGroups.length > 0) {
    if (projectGroupsHaveReferences(existingGroups) || !hasOldFloorplanGroups(floorplans)) {
      return plain;
    }
    // Fall through: groups exist but references are empty while old floorplan-groups still have products
  }

  if (!hasOldFloorplanGroups(floorplans)) return plain;

  const itemFields = scope === BusinessScope.FGDS ? FGDS_ITEM_FIELDS : MONITORING_ITEM_FIELDS;
  const groupMap = new Map<string, Record<string, unknown>>();

  // Migrate floorplans: lift products from floorplan.groups[x].field[] to floorplan.field[]
  const migratedFloorplans = floorplans.map((floorplan) => {
    if (!isRecord(floorplan)) return floorplan;
    const oldGroups = floorplan["groups"];
    if (!Array.isArray(oldGroups) || oldGroups.length === 0) return floorplan;

    const floorplanId = floorplan["id"] as string;
    const liftedItems: Record<string, unknown[]> = {};

    for (const [field] of itemFields) {
      liftedItems[field] = Array.isArray(floorplan[field]) ? [...(floorplan[field] as unknown[])] : [];
    }

    for (const oldGroup of oldGroups) {
      if (!isRecord(oldGroup)) continue;
      const groupId = oldGroup["id"] as string;

      if (!groupMap.has(groupId)) {
        groupMap.set(groupId, {
          id: groupId,
          name: oldGroup["name"],
          groupId: oldGroup["groupId"],
          floorplanIds: [floorplanId],
          references: [],
        });
      } else {
        const floorplanIds = groupMap.get(groupId)!["floorplanIds"] as string[];
        if (!floorplanIds.includes(floorplanId)) {
          floorplanIds.push(floorplanId);
        }
      }

      const references = groupMap.get(groupId)!["references"] as unknown[];
      for (const [field, kind] of itemFields) {
        const items = oldGroup[field];
        if (!Array.isArray(items)) continue;
        for (const item of items) {
          if (!isRecord(item) || typeof item["id"] !== "string") continue;
          // Set configId field if missing (for correct deserialization)
          const liftedItem = { ...item };
          // Mapping: field → configId property name
          const configIdField =
            field === "alarmDevices" || field === "alarmDevicePlaceholders"
              ? "alarmDeviceId"
              : field === "transmitters" || field === "transmitterPlaceholders"
                ? "transmitterId"
                : field === "gasWarningCenters" || field === "gasWarningCenterPlaceholders"
                  ? "gasWarningCenterId"
                  : field === "signalElements" || field === "signalElementPlaceholders"
                    ? "signalElementId"
                    : field === "plasticSigns" || field === "plasticSignPlaceholders"
                      ? "plasticSignId"
                      : field === "utilities" || field === "utilityPlaceholders"
                        ? "utilityId"
                        : undefined;
          if (configIdField && !liftedItem[configIdField]) {
            liftedItem[configIdField] = liftedItem["id"];
          }
          // Lift product to floorplan-level (deduplicate by id)
          const existing = liftedItems[field] as Record<string, unknown>[];
          if (!existing.some((i) => isRecord(i) && i["id"] === liftedItem["id"])) {
            existing.push(liftedItem);
          }
          references.push({ floorplanId, floorplanProductItemId: liftedItem["id"], kind });
        }
      }
    }

    // Remove old groups field, add lifted products
    const rest = { ...floorplan } as Record<string, unknown>;
    delete rest["groups"];
    return { ...rest, ...liftedItems };
  });

  if (groupMap.size === 0) return plain;

  return { ...plain, floorplans: migratedFloorplans, groups: [...groupMap.values()] };
}

/**
 * Migrates duplicate groupId values that can occur when groups from multiple floorplans
 * were merged into project-level groups. Each group previously had a floorplan-scoped
 * groupId (e.g. Floorplan A: groupId 1, Floorplan B: groupId 1). After migration to
 * project-level groups, these must be unique across the project.
 */
function migrateGroupIdsToUniqueProjectScope(plain: Record<string, unknown>): Record<string, unknown> {
  const groups = plain["groups"];
  if (!Array.isArray(groups) || groups.length === 0) return plain;

  const groupIds = groups.map((g) => (isRecord(g) ? (g["groupId"] as number) : undefined));
  const hasDuplicates = groupIds.some((id, idx) => id !== undefined && groupIds.indexOf(id) !== idx);
  if (!hasDuplicates) return plain;

  const reassigned = groups.map((g, idx) => {
    if (!isRecord(g)) return g;
    return { ...g, groupId: idx + 1 };
  });

  return { ...plain, groups: reassigned };
}

/**
 * Ensures `productItemReferences` is populated from `groups[].references` when it is absent or
 * empty. `productItemReferences` is a flat index over all group references, required for efficient
 * look-up and for the duplicate-item logic. It was not always serialized in older project versions,
 * so we rebuild it here to guarantee a consistent state after deserialization.
 */
function backfillProductItemReferences(plain: Record<string, unknown>): Record<string, unknown> {
  const existingRefs = plain["productItemReferences"];
  if (Array.isArray(existingRefs) && existingRefs.length > 0) return plain;

  const groups = plain["groups"];
  if (!Array.isArray(groups) || groups.length === 0) return plain;

  const refs: Record<string, unknown>[] = [];
  const seen = new Set<string>();

  for (const group of groups) {
    if (!isRecord(group)) continue;
    const references = group["references"];
    if (!Array.isArray(references)) continue;
    for (const ref of references) {
      if (!isRecord(ref)) continue;
      const key = `${ref["floorplanId"]}:${ref["floorplanProductItemId"]}`;
      if (!seen.has(key)) {
        seen.add(key);
        refs.push({ floorplanId: ref["floorplanId"], floorplanProductItemId: ref["floorplanProductItemId"], kind: ref["kind"] });
      }
    }
  }

  if (refs.length === 0) return plain;
  return { ...plain, productItemReferences: refs };
}

export function deserializeProject(plain: Record<string, unknown>, scope: BusinessScope): ScopedProject {
  const migratedPlain = backfillProductItemReferences(
    migrateGroupIdsToUniqueProjectScope(migrateOldFloorplanGroupsToProjectGroups(plain, scope)),
  );
  const scopedPayload = sanitizeScopedPayload(migratedPlain, scope);
  if (scope === BusinessScope.FGDS) return plainToInstance(FgdsProject, scopedPayload);
  if (scope === BusinessScope.MONITORING) return plainToInstance(MonitoringProject, scopedPayload);
  throw new Error(`Unknown BusinessScope: "${scope}"`);
}

export function createProjectByScope(scope: BusinessScope, name: string, status: string, country: string): ScopedProject {
  if (scope === BusinessScope.FGDS) return FgdsProject.create(name, status, country);
  if (scope === BusinessScope.MONITORING) return MonitoringProject.create(name, status, country);
  throw new Error(`Unknown BusinessScope: "${scope}"`);
}
